import { UploadFile } from 'antd'
import type { FormInstance } from 'antd/lib'
import axios from 'axios'
import { TFunction } from 'i18next'
import { useCallback, useRef, useState } from 'react'

import { useMediaServiceUploadFilesEndpoint } from 'src/api/queries'
import type { UploadResponse } from 'src/features/counterParts'
import { parseToken } from 'src/utils'

import { message } from '../components/AntdMessages'

type Props = {
  form: FormInstance
  folder: string
}

type UseFileHandlerReturn = {
  existingFiles: string[]
  setExistingFiles: (files: string[]) => void
  onFileChange: (args: { file: UploadFile; fileList: UploadFile[] }) => void
  saveFiles: () => Promise<string[]>
  isUploading: boolean
}

export type FileDocument = UploadFile & { id?: string }

export const useFileHandler = ({
  form,
  folder
}: Props): UseFileHandlerReturn => {
  const [existingFiles, setExistingFiles] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const uploadedByUidRef = useRef<Record<string, string>>({})

  const uploadFiles = useMediaServiceUploadFilesEndpoint<UploadResponse>()

  const onFileChange = useCallback(
    ({ file, fileList }: { file: UploadFile; fileList: UploadFile[] }) => {
      // If a previously saved file is removed, drop its server id from state
      if (file.status === 'removed') {
        setExistingFiles((prev) =>
          prev.filter(
            (fileId) =>
              fileId !== file.uid && fileId !== (file as FileDocument).id
          )
        )
        // Also forget mapping for that uid, in case the user re-adds same local file
        delete uploadedByUidRef.current[file.uid]
      }
      form.setFieldsValue({ docs: fileList })
    },
    [form]
  )

  const saveFiles = useCallback(async () => {
    setIsUploading(true)
    // Take a snapshot of current docs
    const files = (form.getFieldValue(['docs']) ?? []) as FileDocument[]
    const updatedIds = [...existingFiles]

    // Detect if anything changed (new local files or removals of previously saved ones)
    const hasFileChanges =
      files.some((f) => f.originFileObj && !uploadedByUidRef.current[f.uid]) ||
      updatedIds.some(
        (serverId) =>
          !files.some((f) => f.id === serverId || f.uid === serverId)
      )

    if (hasFileChanges) {
      // Upload only files that still have originFileObj and weren’t uploaded before
      const toUpload = files.filter(
        (f) => Boolean(f.originFileObj) && !uploadedByUidRef.current[f.uid]
      )

      if (toUpload.length > 0) {
        try {
          const payloadFiles = toUpload
            .map((f) => f.originFileObj!)
            .filter(Boolean)

          const res = await uploadFiles.mutateAsync({
            folder,
            formData: { files: payloadFiles }
          })

          const newIds = res?.createdResources?.map((r) => r.id) ?? []

          // Map each uploaded file (in order) to returned id
          toUpload.forEach((file, idx) => {
            const serverId = newIds[idx]
            if (!serverId) return

            uploadedByUidRef.current[file.uid] = serverId
            updatedIds.push(serverId)

            // Normalize the corresponding file in form docs:
            const pos = files.findIndex((f) => f.uid === file.uid)
            if (pos >= 0) {
              const normalized: FileDocument = {
                ...files[pos],
                id: serverId,
                uid: serverId, // optional: replace uid with persistent server id
                status: 'done',
                originFileObj: undefined // critical: prevents re-upload next submit
              }
              files[pos] = normalized
            }
          })

          // Persist normalized docs into the form so future submits don't re-upload
          form.setFieldsValue({ docs: files })
          setExistingFiles(updatedIds)
        } catch {
          message.error('File upload failed')
          // Do not clear mappings/ids here; user can fix other errors and resubmit
        }
      }
    }

    setIsUploading(false)
    return Array.from(new Set(updatedIds))
  }, [existingFiles, folder, form, uploadFiles])

  return {
    existingFiles,
    setExistingFiles,
    onFileChange,
    saveFiles,
    isUploading
  }
}

export const onDownloadFile = async (
  t: TFunction,
  id?: string,
  filename?: string
) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_BASE_URL}/api/media/${id}`,
      {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${parseToken(localStorage.getItem('access_token'))}`
        }
      }
    )

    if (response === undefined || response === null) {
      throw new Error('No response from getFileEndpoint')
    }

    const url = window.URL.createObjectURL(
      new Blob([response?.data as BlobPart])
    )
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', filename ?? 'file')
    document.body.appendChild(link)
    link.click()

    link.remove()

    window.URL.revokeObjectURL(url)
  } catch {
    message.error(t('errors.something_went_wrong'))
  }
}
